import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import RegistrationModal from '../components/Modals/RegistrationModal'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { AlertCircle, Building2, Mail, Package, Shield } from 'lucide-react'

const getInviteContext = (invitationData) => {
  const isHubUser = Boolean(invitationData?.isHubUser)
  const email = invitationData?.invitation?.email || invitationData?.email || ''
  const orgName = invitationData?.organization?.name || ''
  const products = Array.isArray(invitationData?.products) ? invitationData.products : []
  const productNames = products.map((item) => item?.name).filter(Boolean)
  const productName = productNames[0] || ''
  const productLabel = productNames.length > 1 ? productNames.join(', ') : productName
  const roleName =
    invitationData?.product_role?.name ||
    invitationData?.role?.name ||
    invitationData?.invitation?.role?.name ||
    ''
  const markSource = productLabel || orgName || 'Invite'

  return {
    isHubUser,
    email,
    orgName,
    productName,
    productLabel,
    roleName,
    mark: markSource.trim().charAt(0).toUpperCase() || 'I',
    heading: isHubUser ? 'Hub access' : productLabel || orgName || 'your workspace'
  }
}

const InviteShell = ({ children }) => (
  <div className="relative min-h-screen overflow-hidden bg-[#efeae2]">
    <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-[#e4ddd2] to-transparent" />
    <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8 sm:py-12">
      {children}
    </div>
  </div>
)

const Fact = ({ icon: Icon, label, value }) => {
  if (!value) return null
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-white/55" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.16em] text-white/45">{label}</p>
        <p className="mt-0.5 truncate text-sm font-medium text-white">{value}</p>
      </div>
    </div>
  )
}

const AcceptInvitation = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [invitationData, setInvitationData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRegistration, setShowRegistration] = useState(false)

  useEffect(() => {
    const previous = document.title
    document.title = 'Invitation'
    return () => {
      document.title = previous
    }
  }, [])

  useEffect(() => {
    validateInvitation()
  }, [token])

  const validateInvitation = async () => {
    try {
      setLoading(true)
      setError(null)

      const rawBase = import.meta.env.VITE_API_URL || 'http://localhost:5000'
      const normalizedBase = rawBase.endsWith('/api')
        ? rawBase.replace(/\/$/, '')
        : `${rawBase.replace(/\/$/, '')}/api`

      const response = await fetch(`${normalizedBase}/invitations/token/${token}`)
      const data = await response.json()

      if (data.success) {
        setInvitationData(data.data)
        setShowRegistration(true)
      } else {
        const errorMessage = data.message || 'Invalid invitation'
        if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('no longer valid')) {
          setError('This invitation link has expired. Please contact your administrator to request a new invitation.')
        } else {
          setError(errorMessage)
        }
      }
    } catch (err) {
      setError(`Network error: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRegistrationSuccess = (user, productLoginUrl) => {
    if (productLoginUrl) {
      window.location.href = productLoginUrl
      return
    }

    navigate('/login', {
      state: {
        message: 'Registration successful! Please login with your new account.',
        email: user.email
      }
    })
  }

  const context = getInviteContext(invitationData)
  const isExpired = Boolean(error && error.toLowerCase().includes('expired'))

  if (loading) {
    return (
      <InviteShell>
        <div className="w-full max-w-md rounded-[32px] bg-white px-8 py-16 text-center shadow-[0_30px_80px_rgba(60,40,20,0.12)]">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm font-medium text-stone-600">Opening your invitation</p>
        </div>
      </InviteShell>
    )
  }

  if (error) {
    return (
      <InviteShell>
        <div className="w-full max-w-md rounded-[32px] bg-white px-8 py-9 shadow-[0_30px_80px_rgba(60,40,20,0.12)]">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-400">Invitation</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-stone-900">
            {isExpired ? 'This link expired' : 'Invite unavailable'}
          </h1>
          <div className="mt-5 flex gap-3 rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="leading-relaxed">{error}</p>
          </div>
        </div>
      </InviteShell>
    )
  }

  return (
    <InviteShell>
      <div className="grid w-full max-w-[920px] overflow-hidden rounded-[32px] bg-white shadow-[0_30px_80px_rgba(60,40,20,0.12)] lg:grid-cols-[0.92fr_1.08fr]">
        <aside className="relative bg-[#1f3a32] px-8 py-10 text-white sm:px-10">
          <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-[#d7b56d]/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-44 w-44 rounded-full bg-white/5 blur-2xl" />

          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-white/10 text-2xl font-semibold">
              {context.mark}
            </div>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-white/50">
              You're invited
            </p>
            <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight">
              {context.heading}
            </h1>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/65">
              {context.isHubUser
                ? 'Create a password to finish your account.'
                : `Join ${context.orgName || 'your organization'} and start using the product.`}
            </p>

            <div className="mt-10 space-y-5 border-t border-white/10 pt-8">
              <Fact icon={Mail} label="Email" value={context.email} />
              {!context.isHubUser && <Fact icon={Package} label="Product" value={context.productLabel} />}
              <Fact
                icon={Building2}
                label={context.isHubUser ? 'Workspace' : 'Organization'}
                value={context.isHubUser ? 'Hub access' : context.orgName}
              />
              <Fact icon={Shield} label="Role" value={context.roleName} />
            </div>
          </div>
        </aside>

        <section className="flex flex-col justify-center bg-[#fbfaf7] px-6 py-10 sm:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-400">Almost there</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-stone-900">
            Create your password
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-500">
            Use this password the next time you sign in to the product.
          </p>

          <div className="mt-8">
            <RegistrationModal
              embedded
              hideSummary
              submitClassName="!bg-[#1f3a32] hover:!bg-[#18312b] !text-white"
              isOpen={showRegistration}
              onClose={() => {}}
              invitationData={invitationData}
              token={token}
              onSuccess={(user, productLoginUrl) => handleRegistrationSuccess(user, productLoginUrl)}
            />
          </div>
        </section>
      </div>
    </InviteShell>
  )
}

export default AcceptInvitation
